/** A hash router in thirty lines.
 *
 * Hash routing is a deliberate choice: it needs no SPA-fallback rewrite rule,
 * so the same `dist/` folder works unchanged on Cloudflare Pages, Vercel,
 * GitHub Pages under a subpath, or any plain static file server. That is what
 * keeps the app genuinely portable between hosts.
 */

export type Route =
  /** `q` is the search box's text, kept in the URL so Back returns to the
   *  results rather than an empty search. */
  | { name: 'library'; folderId?: string; q?: string }
  | { name: 'player'; bookId: string }
  | { name: 'settings' };

export function parseHash(hash: string): Route {
  const [path = '', search = ''] = hash.replace(/^#\/?/, '').split('?');
  const [head, tail] = path.split('/');
  if (head === 'player' && tail) return { name: 'player', bookId: decodeURIComponent(tail) };
  if (head === 'settings') return { name: 'settings' };

  const q = new URLSearchParams(search).get('q');
  return {
    name: 'library',
    ...(head === 'library' && tail ? { folderId: decodeURIComponent(tail) } : {}),
    ...(q ? { q } : {}),
  };
}

export function toHash(route: Route): string {
  switch (route.name) {
    case 'player':
      return `#/player/${encodeURIComponent(route.bookId)}`;
    case 'settings':
      return '#/settings';
    default: {
      const path = route.folderId ? `#/library/${encodeURIComponent(route.folderId)}` : '#/library';
      return route.q ? `${path}?${new URLSearchParams({ q: route.q })}` : path;
    }
  }
}

/** Distinct per screen, but stable while you stay on it: a search typed into
 *  the library is the same screen; another folder or book is not. */
export function screenKey(route: Route): string {
  if (route.name === 'player') return `player:${route.bookId}`;
  if (route.name === 'library' && route.folderId) return `library:${route.folderId}`;
  return route.name;
}

export function currentRoute(): Route {
  return parseHash(window.location.hash);
}

/** `replace` rewrites the current history entry instead of adding one — used
 *  while typing a search, so Back does not step through it letter by letter. */
export function navigateTo(route: Route, { replace = false } = {}): void {
  const next = toHash(route);
  if (window.location.hash === next) return;
  if (replace) window.history.replaceState(window.history.state, '', next);
  else window.location.hash = next;
}

export function onRouteChange(handler: (route: Route) => void): () => void {
  const listener = () => handler(currentRoute());
  window.addEventListener('hashchange', listener);
  return () => window.removeEventListener('hashchange', listener);
}
